import { GridTile } from '../../../../src/shared/components/grid-tile.js';
import { artworkCoverUrl, setFavorite } from '../api.js';
import { openAddToListModal } from './add-to-list-modal.js';
import { isQueued, setQueued } from '../queue-state.js';
import { state } from '../state.js';

/**
 * BYO-ROM lock: when the `byo_rom_mode` master switch is on and the catalog
 * marked this game as not playable (the user doesn't own a matching upload),
 * the tile shows a lock. Launch still funnels into the main app, which prompts
 * a BYO upload on the gated 403. Checks `=== true` so an absent/unreachable
 * flag never locks the library (the flag system is fail-open).
 *
 * The lock is a bundled-tile affordance ("upload your ROM to play") and must
 * never stack onto a `user_rom` row — a synthetic's `playable: false` means
 * something else (an unavailable stale claim), and it renders its own
 * "UNAVAILABLE" chip instead. Owned rows are never locked either way, but
 * this exclusion is what keeps the two chips out of each other's way when a
 * stale claim happens to also have `playable: false`.
 */
function isLocked(rom) {
  return state.get().features?.byo_rom_mode === true
    && rom.playable === false
    && rom.user_rom !== true;
}

/**
 * Library-flavoured GridTile. Thin wrapper around the shared
 * `src/shared/components/grid-tile.js` that injects the library's
 * server-side plumbing: artwork URLs, favorite toggle, add-to-list
 * modal.
 *
 * The shared tile is intentionally pure-props so the homepage can
 * reuse it without dragging the library api bundle along. The library
 * keeps its existing callsite shape via this wrapper — pass the same
 * {rom, onPlay, onDetail} the old code passed. `showActions: false`
 * keeps only the detail link for focused contexts such as list editing.
 */
export function libraryGameHref(rom) {
  const system = rom.game_system || rom.system;
  const slug = rom.game_slug || rom.game_id || rom.hash;
  if (!system || slug == null || slug === '') return null;
  return `/library/g/${encodeURIComponent(system)}/${encodeURIComponent(String(slug))}`;
}

/**
 * Detail-route href for a /api/trending/games entry. The trending API
 * returns flat `system_id` / `slug` columns straight off the games table,
 * not the catalog's `game_system` / `game_slug` enrichment fields — map
 * them into the canonical builder so the two-segment URL shape stays
 * defined in one place, with the same game_id fallback for games rows
 * that have no slug.
 */
export function trendingGameHref(game) {
  if (!game) return null;
  return libraryGameHref({
    game_system: game.system_id,
    game_slug: game.slug,
    game_id: game.game_id,
  });
}

export function LibraryGridTile({
  rom,
  onPlay,
  onDetail,
  onReport,
  onRemove,
  showActions = true,
}) {
  return GridTile({
    rom,
    coverUrl: rom.game_id != null ? artworkCoverUrl(rom.game_id) : null,
    // An unmatched synthetic (a "My ROMs" upload with no catalog match) has
    // no /library/g/<system>/<slug> page to link to — libraryGameHref falls
    // back to the content hash as the slug, which would 404.
    detailHref: (rom.user_rom && rom.game_id == null) ? null : libraryGameHref(rom),
    owned: rom.owned === true,
    onPlay: showActions ? onPlay : undefined,
    onDetail,
    onReport: showActions ? onReport : undefined,
    onRemove: (showActions && onRemove) ? onRemove : undefined,
    onFavorite: showActions ? (r, next) => setFavorite(r.system, r.hash, next) : undefined,
    onAddToList: showActions
      ? ({ gameId, title }) => openAddToListModal({ gameId, title })
      : undefined,
    queued: rom.game_id != null ? isQueued(rom.game_id) : false,
    onQueueToggle: showActions ? (r, next) => setQueued(r.game_id, next) : undefined,
    locked: isLocked(rom),
  });
}
